import React, { useEffect, useMemo, useState } from 'react';
import type { CustomField } from '../../services/queries/fieldQueries';
import { persistedClientRecords } from '../../services/mockData/clients';
import { persistedContactRecords } from '../../services/mockData/contacts';
import { persistedOpportunityRecords } from '../../services/mockData/opportunities';
import { useUsers } from '../../services/queries/userQueries';
import { getGobodoRecordById, searchRecordsForLookupField } from '../../services/gobodo/gobodoRecordService';
import { searchCreditBlockAssigneeOptions } from '../../services/gobodo/creditBlockNewCaseGobodoSearch';
import { getRecordDisplayName } from '../../utils/recordDisplayName';
import { USE_GOBODO_API } from '../../utils/gobodoUtils';
import { AsyncSearchableSelect } from '../modals/AsyncSearchableSelect';
import { SearchableSelect } from '../modals/SearchableSelect';
type LookupOption = {
  value: string;
  label: string;
};

export interface StandardLookupSelection {
  value: string;
  label: string;
  field: CustomField;
}

export interface StandardLookupFieldProps {
  field: CustomField;
  value: string;
  onChange: (value: string) => void;
  onLookupChange?: (selection: StandardLookupSelection | null) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  error?: string;
  disabled?: boolean;
  selectedOptionLabel?: string;
  /** Hide clear button and chevron for inline edit contexts where parent provides save/cancel. */
  compact?: boolean;
  /**
   * Optional override for user-lookup typeahead. When provided AND the field
   * is a User lookup, the autocomplete filters this list client-side instead
   * of calling searchCreditBlockAssigneeOptions (which hits Gobodo's full
   * user search and returns every active user — wrong for surfaces that
   * should be scoped to a specific role, e.g. Case Owner = CB team only).
   */
  userOptionsOverride?: LookupOption[];
}

const normalizeLookupObject = (lookupObject?: string): string => {
  return String(lookupObject ?? '').trim().toLowerCase();
};

const isUserLookupObject = (lookupObject?: string): boolean => {
  const normalized = normalizeLookupObject(lookupObject);
  return normalized === 'user' || normalized === 'user__c';
};

export const StandardLookupField: React.FC<StandardLookupFieldProps> = ({
  field,
  value,
  onChange,
  onLookupChange,
  label,
  placeholder,
  required,
  error,
  disabled = false,
  selectedOptionLabel,
  compact = false,
  userOptionsOverride,
}) => {
  const [resolvedLabel, setResolvedLabel] = useState(selectedOptionLabel || '');
  const { data: users } = useUsers({ status: 'active' });

  const localOptions = useMemo<LookupOption[]>(() => {
    const lookupObject = normalizeLookupObject(field.lookupObject);

    if (lookupObject === 'client' || lookupObject === 'account' || lookupObject === 'client__c' || lookupObject === 'account__c') {
      return persistedClientRecords.get().map((client) => ({
        value: String(client.id),
        label: String(client.name ?? client.id),
      }));
    }

    if (lookupObject === 'contact' || lookupObject === 'contact__c') {
      return persistedContactRecords.get().map((contact) => ({
        value: String(contact.id),
        label: `${contact.firstName} ${contact.lastName}`.trim(),
      }));
    }

    if (lookupObject === 'opportunity' || lookupObject === 'opportunity__c') {
      return persistedOpportunityRecords.get().map((opportunity) => ({
        value: String(opportunity.id),
        label: String(opportunity.opportunityName ?? opportunity.id),
      }));
    }

    if (isUserLookupObject(field.lookupObject)) {
      return (users ?? []).map((user) => ({
        value: String(user.graphObjectKey ?? user.id),
        label: `${user.firstName} ${user.lastName}`.trim(),
      }));
    }

    return [];
  }, [field.lookupObject, users]);

  const fallbackOptions = useMemo<LookupOption[]>(() => {
    const rawValue = String(value ?? '').trim();
    if (!rawValue) return localOptions;
    if (localOptions.some((option) => option.value === rawValue)) return localOptions;
    return [
      ...localOptions,
      {
        value: rawValue,
        label: resolvedLabel || rawValue,
      },
    ];
  }, [localOptions, resolvedLabel, value]);

  useEffect(() => {
    if (selectedOptionLabel) {
      setResolvedLabel(selectedOptionLabel);
      return;
    }

    const rawValue = String(value ?? '').trim();
    if (!rawValue) {
      setResolvedLabel('');
      return;
    }

    const matchedLocalOption = localOptions.find((candidate) => candidate.value === rawValue);
    if (matchedLocalOption?.label) {
      setResolvedLabel(matchedLocalOption.label);
      return;
    }

    if (!USE_GOBODO_API) {
      setResolvedLabel(rawValue);
      return;
    }

    const looksLikeGraphRecordId = /^\d+$/.test(rawValue) || rawValue.startsWith('rec_');
    if (!looksLikeGraphRecordId) {
      setResolvedLabel(rawValue);
      return;
    }

    let cancelled = false;
    getGobodoRecordById(rawValue)
      .then((record) => {
        if (!cancelled) {
          setResolvedLabel(record ? getRecordDisplayName(field.lookupObject || '', record) : rawValue);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setResolvedLabel(rawValue);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [field.lookupObject, localOptions, selectedOptionLabel, value]);

  const handleLookupSearch = async (query: string): Promise<LookupOption[]> => {
    try {
      if (isUserLookupObject(field.lookupObject)) {
        // If a pre-filtered override list was provided (e.g. CB Case Owner
        // scoped to Team Lead + Processor), filter that list locally instead
        // of hitting the unfiltered Gobodo user search.
        if (userOptionsOverride && userOptionsOverride.length > 0) {
          const q = query.trim().toLowerCase();
          if (!q) return userOptionsOverride;
          return userOptionsOverride.filter((option) =>
            option.label.toLowerCase().includes(q) || option.value.toLowerCase().includes(q),
          );
        }
        const results = await searchCreditBlockAssigneeOptions(query);
        return results.map((option) => ({
          value: String(option.value).replace(/^u:/, ''),
          label: option.label,
        }));
      }
      const records = await searchRecordsForLookupField(field, query);
      return records.map((record) => ({
        value: String(record.key ?? (record as { graphObjectKey?: unknown }).graphObjectKey ?? record.id),
        label: getRecordDisplayName(field.lookupObject || '', record),
      }));
    } catch (lookupError) {
      console.error('[StandardLookupField] Search error:', lookupError);
      return [];
    }
  };

  // Use ?? so empty string label hides the label (|| treats "" as falsy)
  const effectiveLabel = label ?? field.fieldLabel;
  const effectivePlaceholder = placeholder || (effectiveLabel ? `Search ${effectiveLabel}...` : 'Search...');
  const emitLookupChange = (option: LookupOption | null) => {
    onLookupChange?.(
      option
        ? {
            value: option.value,
            label: option.label,
            field,
          }
        : null
    );
  };

  if (USE_GOBODO_API && (field.relTypeId || field.lookupObject)) {
    return (
      <AsyncSearchableSelect
        label={effectiveLabel}
        name={field.fieldName}
        value={String(value || '')}
        onChange={onChange}
        onSearch={handleLookupSearch}
        placeholder={effectivePlaceholder}
        error={error}
        required={required ?? field.required}
        disabled={disabled}
        selectedOptionLabel={resolvedLabel}
        onOptionSelect={emitLookupChange}
        minSearchLength={1}
        debounceMs={300}
        compact={compact}
      />
    );
  }

  return (
    <SearchableSelect
      label={effectiveLabel}
      name={field.fieldName}
      value={String(value || '')}
      onChange={onChange}
      options={fallbackOptions}
      placeholder={effectivePlaceholder}
      error={error}
      required={required ?? field.required}
      disabled={disabled}
      onOptionSelect={emitLookupChange}
    />
  );
};
